import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { RenderJob } from '../../../../core/render-runtime/worker/render-job.model';
import { SocialReviewTargetComplete } from '../../../Social/services/social-island.service';
import { SocialTemplateRenderHandoffService } from '../../../Social/services/social-template-render-handoff.service';
import { JivuCommandProgressReporter } from '../../../JivuCommand/services/jivu-command-progress-reporter.service';
import { JivuCommandPollerService } from '../../../JivuCommand/services/jivu-command-poller.service';
import { dateKeyFromStorageKey, reviewTabFromResult } from '../../../JivuCommand/services/jivu-command-review-result';
import { JivuCommandService } from '../../../JivuCommand/services/jivu-command.service';

/** What the page hands over to drive a Template-Use command render. Mirrors the handoff request minus onProgress. */
export interface TemplateUseCommandRenderRequest {
  postId: string;
  job: RenderJob;
  templateId?: string | null;
  name: string;
  durationSeconds: number;
  caption?: string;
  script: string;
}

const COMMAND_RENDER_PROGRESS_START = 45;
const COMMAND_RENDER_PROGRESS_END = 98;

/**
 * Root-level Template-Use command render driver.
 *
 * Wave 2a of the render refactor: relocates the page-bound ORCHESTRATION (render → upload →
 * complete/fail the Jivu Command) for the `template_use_render` flow OUT of the TemplateUseModal
 * (which is destroyed when the user navigates away from / closes the Template-Use modal) and INTO
 * this `providedIn: 'root'` singleton. Because the service lives at the application root, a
 * Template-Use command keeps rendering, uploading, and completing even after the modal is gone.
 *
 * STRICTLY ADDITIVE. The render + R2 reserve/sign/upload still happen inside
 * `SocialTemplateRenderHandoffService.renderAndUploadToReview` (reused as-is — R2 layout frozen).
 * The non-command (direct render) page path in TemplateUseModal is untouched; only the command
 * case delegates here. The UI job (`buildTemplateRenderJob`) is still built on the page and handed
 * over as a serializable `RenderJob`.
 */
@Injectable({ providedIn: 'root' })
export class TemplateUseCommandRenderService {
  private readonly renderHandoff = inject(SocialTemplateRenderHandoffService);
  private readonly progressReporter = inject(JivuCommandProgressReporter);
  private readonly command = inject(JivuCommandService);
  private readonly poller = inject(JivuCommandPollerService);

  /**
   * Command ids currently being driven — guards against double-drive (e.g. a re-entrant render click)
   * AND tracks what is cancellable. `cancel()` is the background-worker handle's `.cancel`, populated
   * once the render starts; `aborted` is checked when the await resolves so a cancel that lands after
   * the render finishes still skips the command complete.
   */
  private readonly inFlight = new Map<string, { cancel: (() => void) | null; aborted: boolean }>();

  /**
   * Drive a Template-Use command end-to-end: render the prebuilt job + upload it to Review via the
   * root handoff service (upload is inside), mirroring render progress into the command, then
   * complete or fail the command. Survives modal/route teardown because this service is root-scoped.
   */
  async drive(commandId: string, request: TemplateUseCommandRenderRequest): Promise<void> {
    const id = (commandId || '').trim();
    if (!id || this.inFlight.has(id)) return;
    const entry = { cancel: null as (() => void) | null, aborted: false };
    this.inFlight.set(id, entry);

    try {
      const response: SocialReviewTargetComplete = await this.renderHandoff.renderAndUploadToReview({
        postId: request.postId,
        job: request.job,
        templateId: request.templateId,
        name: request.name,
        durationSeconds: request.durationSeconds,
        caption: request.caption,
        script: request.script,
        onHandle: cancel => { entry.cancel = cancel; },
        onProgress: (percent, message) => this.progressReporter.report(
          id,
          this.commandRenderProgress(percent),
          message
        )
      });

      // Cancelled mid/after-render: the cancel path already marked the command cancelled — skip the
      // command complete (the render/upload may still have finished, but the user cancelled).
      if (entry.aborted) {
        this.command.clearCurrentHandoffFor(id);
        return;
      }

      await firstValueFrom(this.command.complete(
        id,
        {
          reviewId: response.reviewId,
          reviewType: response.reviewType,
          driveFileId: response.driveFileId ?? null,
          reviewTab: reviewTabFromResult({ reviewType: response.reviewType }, response.driveFileId),
          dateKey: dateKeyFromStorageKey(response.driveFileId),
          previewType: reviewTabFromResult({ reviewType: response.reviewType }, response.driveFileId) === 'shorts'
            ? 'shorts'
            : response.reviewType,
          templateId: request.templateId,
          title: request.name
        },
        'Template Use rendered to Review on target device.'
      ));
      this.command.clearCurrentHandoffFor(id);
      this.poller.claimNextQueuedCommandSoon(id);
    } catch (err) {
      // A cancel aborts the worker, which rejects the render promise — that is expected teardown,
      // not a failure. The cancel path already marked the command cancelled, so swallow it.
      if (entry.aborted) {
        this.command.clearCurrentHandoffFor(id);
        return;
      }
      await firstValueFrom(this.command.fail(id, this.errorMessage(err)));
      this.command.clearCurrentHandoffFor(id);
      this.poller.claimNextQueuedCommandSoon(id);
    } finally {
      this.inFlight.delete(id);
    }
  }

  /**
   * Abort the in-flight client render for this command (Wave 5). Sets the abort flag (so a render
   * that is already finishing skips the command complete) and stops the background worker via its
   * stored `.cancel`. The existing cancel cascade (BE job + command record) is handled by the caller;
   * this only kills the LOCAL render. No-op if the command is not being driven here.
   */
  cancel(commandId: string): void {
    const id = (commandId || '').trim();
    const entry = this.inFlight.get(id);
    if (!entry) return;
    entry.aborted = true;
    try {
      entry.cancel?.();
    } catch {
      // Best effort — the worker may already be gone.
    }
    this.progressReporter.clear(id);
    this.command.clearCurrentHandoffFor(id);
    this.inFlight.delete(id);
  }

  private errorMessage(err: unknown): string {
    return err instanceof Error ? err.message : 'Template render failed.';
  }

  private commandRenderProgress(percent: number): number {
    const raw = Number.isFinite(percent) ? percent : 0;
    const clamped = Math.max(0, Math.min(100, raw));
    const span = COMMAND_RENDER_PROGRESS_END - COMMAND_RENDER_PROGRESS_START;
    return Math.round(COMMAND_RENDER_PROGRESS_START + (clamped / 100) * span);
  }
}
